import type { InputProps as MUIInputProps } from '@mui/material/Input';
import TextField from '@mui/material/TextField';
import { noop } from 'lodash';
import {
	ChangeEvent,
	KeyboardEventHandler,
	useImperativeHandle,
	useState,
} from 'react';

import { useFocus } from 'library/hooks/useFocus';
import { Readonly, Sxable } from 'library/types';

interface InternalInputProps {
	isError?: boolean;
}

export interface CustomInputHandle {
	focus: () => void;
	getValue: () => string;
	reset: () => void;
}

export interface InputProps extends Sxable, Readonly {
	className?: string;
	defaultValue?: string;
	InputProps?: MUIInputProps;
	label: string;
	onChange?: (value: string) => void | 'reject-change';
	onKeyPress?: KeyboardEventHandler<HTMLDivElement>;
	ref?: React.RefObject<CustomInputHandle | null>;
}

export const Input = ({
	isError = false,
	isReadonly,
	onChange = noop,
	onKeyPress = noop,
	sx,
	ref,
	...restProps
}: InputProps & InternalInputProps): React.JSX.Element => {
	const [focusRef, setFocusRef] = useFocus();
	const [value, setValue] = useState('');

	useImperativeHandle(
		ref,
		() => ({
			focus: setFocusRef,
			getValue: () => value,
			reset: () => {
				setValue('');
			},
		}),
		[setFocusRef, value]
	);

	const handleChange = ({
		target: { value },
	}: ChangeEvent<HTMLInputElement>) => {
		onChange(value);
		setValue(value);
	};

	return (
		<TextField
			fullWidth
			error={isError}
			disabled={isReadonly}
			inputRef={focusRef}
			sx={[
				{
					'&>.MuiInputBase-root': {
						backgroundColor: 'background.paper',
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			onChange={handleChange}
			onKeyPress={onKeyPress}
			value={value}
			{...restProps}
		/>
	);
};
